import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Package, DollarSign, TrendingUp } from 'lucide-react';
import apiClient from '@/services/api';
import type { MenuBreakdownReport } from '@/types/api';
import { KPICard } from '@/components/reports/KPICard';
import { ReportFrame, SimpleTable } from '@/components/reports/ReportFrame';
import type { ReportViewProps } from '@/components/reports/reportTypes';
import { buildGenericCsv, downloadCsv, printGenericReport, type ReportSection } from '@/utils/reportExport';
import { ItemNameFilter } from '@/components/reports/ItemNameFilter';

/** Items Statistics: per-item quantity, revenue, average price and share, plus a category rollup. */
export default function ItemsStatisticsReport({ filters }: ReportViewProps) {
  const { selectedRestaurantId, startDate, endDate, formatCurrency, selectedRestaurant } = filters;

  const { data, isLoading, isError, refetch } = useQuery<MenuBreakdownReport>({
    queryKey: ['menu-breakdown', selectedRestaurantId, startDate, endDate],
    queryFn: () => apiClient.getMenuBreakdown(selectedRestaurantId!, startDate, endDate),
    enabled: !!selectedRestaurantId,
  });

  const allItems = data?.items ?? [];

  // Reset the picked-items filter whenever the underlying report data changes.
  const [selectedNames, setSelectedNames] = useState<string[]>([]);
  useEffect(() => setSelectedNames([]), [selectedRestaurantId, startDate, endDate]);

  const items = selectedNames.length === 0 ? allItems : allItems.filter((i) => selectedNames.includes(i.name));

  const categories = useMemo(() => {
    const byCategory = new Map<string, { name: string; quantity: number; revenue: number }>();
    items.forEach((i) => {
      const key = i.category || 'Uncategorized';
      const row = byCategory.get(key) ?? { name: key, quantity: 0, revenue: 0 };
      row.quantity += Number(i.quantity);
      row.revenue += Number(i.revenue);
      byCategory.set(key, row);
    });
    return Array.from(byCategory.values());
  }, [items]);

  const totalQty = items.reduce((a, i) => a + Number(i.quantity), 0);
  const totalRev = items.reduce((a, i) => a + Number(i.revenue), 0);
  const avgPrice = totalQty > 0 ? totalRev / totalQty : 0;
  const unitPrice = (rev: number, qty: number) => (qty > 0 ? rev / qty : 0);
  const share = (qty: number) => (totalQty > 0 ? `${((qty / totalQty) * 100).toFixed(1)}%` : '—');
  const meta = () => ({ restaurantName: selectedRestaurant?.name ?? '', startDate, endDate, formatCurrency });

  const sections = (): ReportSection[] => [
    {
      heading: 'By Category',
      headers: ['Category', 'Qty', 'Revenue', 'Avg Price'],
      rows: categories.map((c) => [c.name, Number(c.quantity).toLocaleString(), formatCurrency(c.revenue), formatCurrency(unitPrice(Number(c.revenue), Number(c.quantity)))]),
    },
    {
      heading: 'Item Statistics',
      headers: ['Item', 'Category', 'Qty', 'Revenue', 'Avg Price', 'Qty Share'],
      rows: items.map((i) => [i.name, i.category, Number(i.quantity).toLocaleString(), formatCurrency(i.revenue), formatCurrency(unitPrice(Number(i.revenue), Number(i.quantity))), share(Number(i.quantity))]),
    },
  ];
  const kpis = (): [string, string][] => [
    ['Items Sold', totalQty.toLocaleString()],
    ['Revenue', formatCurrency(totalRev)],
    ['Avg Item Price', formatCurrency(avgPrice)],
  ];

  return (
    <ReportFrame
      title="Items Statistics"
      description="Per-item quantity, revenue, average price and share — plus a category rollup."
      hasRestaurant={!!selectedRestaurantId}
      isLoading={isLoading}
      isError={isError}
      onRetry={() => refetch()}
      isEmpty={allItems.length === 0}
      exportDisabled={!data}
      onExportCsv={() => data && downloadCsv(`items-statistics-${format(new Date(), 'yyyy-MM-dd')}.csv`, buildGenericCsv('Items Statistics', meta(), kpis(), sections()))}
      onExportPdf={() => data && printGenericReport('Items Statistics Report', meta(), kpis(), sections())}
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KPICard label="Items Sold" value={totalQty.toLocaleString()} icon={Package} palette="blue" />
        <KPICard label="Revenue" value={formatCurrency(totalRev)} icon={DollarSign} palette="green" />
        <KPICard label="Avg Item Price" value={formatCurrency(avgPrice)} icon={TrendingUp} palette="purple" />
      </div>
      <div className="card"><div className="card-body">
        <ItemNameFilter items={allItems} selected={selectedNames} onChange={setSelectedNames} />
      </div></div>
      {items.length === 0 ? (
        <div className="card"><div className="card-body text-center text-gray-500">No items match the current filter</div></div>
      ) : (
        <>
          <div className="card"><div className="card-body">
            <h3 className="text-lg font-semibold mb-4">By Category</h3>
            <SimpleTable headers={['Category', 'Qty', 'Revenue', 'Avg Price']}
              rows={categories.map((c) => [c.name, Number(c.quantity).toLocaleString(), formatCurrency(c.revenue), formatCurrency(unitPrice(Number(c.revenue), Number(c.quantity)))])} />
          </div></div>
          <div className="card"><div className="card-body">
            <h3 className="text-lg font-semibold mb-4">Item Statistics ({items.length})</h3>
            <SimpleTable headers={['Item', 'Category', 'Qty', 'Revenue', 'Avg Price', 'Qty Share']}
              rows={items.map((i) => [i.name, i.category, Number(i.quantity).toLocaleString(), formatCurrency(i.revenue), formatCurrency(unitPrice(Number(i.revenue), Number(i.quantity))), share(Number(i.quantity))])} />
          </div></div>
        </>
      )}
    </ReportFrame>
  );
}
